import { useState, useEffect } from 'react';
import { Chip, Tooltip } from '@material-ui/core';
import { TableColumn } from '@backstage/core-components';
import { CatalogTableRow } from '@backstage/plugin-catalog';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';

const ANNOTATION_PREFIX = 'salestrak.io/version-url';

const versionCache = new Map<
  string,
  { version: string | null; error: boolean }
>();

function VersionCell({ url }: { url: string | undefined }) {
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch: backstageFetch } = useApi(fetchApiRef);
  const [version, setVersion] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!url) return;

    const cached = versionCache.get(url);
    if (cached) {
      setVersion(cached.version);
      setError(cached.error);
      return;
    }

    setLoading(true);
    let cancelled = false;

    discoveryApi
      .getBaseUrl('service-versions')
      .then(baseUrl =>
        backstageFetch(
          `${baseUrl}/probe?url=${encodeURIComponent(url)}`,
        ),
      )
      .then(res => {
        if (!res.ok) throw new Error(`${res.status}`);
        return res.json();
      })
      .then(data => {
        if (cancelled) return;
        const v = data.version ?? data.Version ?? JSON.stringify(data);
        versionCache.set(url, { version: String(v), error: false });
        setVersion(String(v));
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        versionCache.set(url, { version: null, error: true });
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url, discoveryApi, backstageFetch]);

  if (!url) {
    return <span style={{ color: '#888' }}>&mdash;</span>;
  }
  if (loading) {
    return <span style={{ color: '#888' }}>...</span>;
  }
  if (error) {
    return (
      <Tooltip title={`Failed to fetch ${url}/version`}>
        <Chip label="err" size="small" color="secondary" variant="outlined" />
      </Tooltip>
    );
  }

  return (
    <Chip
      label={version}
      size="small"
      variant="outlined"
      style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
    />
  );
}

export function createVersionColumn(
  env: string,
  title: string,
): TableColumn<CatalogTableRow> {
  return {
    title,
    field: `version-${env}`,
    render: (row: CatalogTableRow) => {
      const url =
        row.entity.metadata.annotations?.[`${ANNOTATION_PREFIX}-${env}`];
      return <VersionCell url={url} />;
    },
    sorting: false,
    width: 'auto',
  };
}
